/**
 * Custom-Metric Templates (Story 16.7)
 *
 * A STATIC bank of common blended/derived metrics. Selecting one seeds the
 * report editor's CustomMetricConfigSection draft (formula + variables + output
 * format); the user then customizes the metricKeys/integrations for their own
 * connected platforms before adding/saving.
 *
 * v1 is a module-level constant (zero DB) — user-saved templates would build on
 * the Story 16.3 custom_metrics table (out of scope here). This is DISTINCT from
 * the report-LAYOUT template system (TemplateCard / useTemplateMutations).
 *
 * Grammar: only `/` and `* <literal>` are used, so every template validates
 * under the current formula engine (the `%` operator + SUM/AVG/MAX/MIN functions
 * are Story 16.6 and are not needed here). Variable ids match
 * /^[a-zA-Z_]\w*$/ and the formula references only declared ids.
 */
export interface CustomMetricTemplateVariable {
  id: string;
  label: string;
  metricKey: string;
}

export interface CustomMetricTemplate {
  key: string;
  label: string;
  description: string;
  formulaExpression: string;
  variables: CustomMetricTemplateVariable[];
  outputFormat: "number" | "currency" | "percentage";
}

export const CUSTOM_METRIC_TEMPLATES: CustomMetricTemplate[] = [
  {
    key: "roas",
    label: "ROAS",
    description: "Return on ad spend — Revenue ÷ Ad Spend",
    formulaExpression: "revenue / ad_spend",
    variables: [
      { id: "revenue", label: "Revenue", metricKey: "purchaseRevenue" },
      { id: "ad_spend", label: "Ad Spend", metricKey: "cost" },
    ],
    outputFormat: "number",
  },
  {
    key: "cpa",
    label: "CPA",
    description: "Cost per acquisition — Ad Spend ÷ Conversions",
    formulaExpression: "ad_spend / conversions",
    variables: [
      { id: "ad_spend", label: "Ad Spend", metricKey: "cost" },
      { id: "conversions", label: "Conversions", metricKey: "conversions" },
    ],
    outputFormat: "currency",
  },
  {
    key: "cpc",
    label: "CPC",
    description: "Cost per click — Ad Spend ÷ Clicks",
    formulaExpression: "ad_spend / clicks",
    variables: [
      { id: "ad_spend", label: "Ad Spend", metricKey: "cost" },
      { id: "clicks", label: "Clicks", metricKey: "clicks" },
    ],
    outputFormat: "currency",
  },
  {
    key: "blended_cpm",
    label: "Blended CPM",
    description: "Cost per 1,000 impressions — Ad Spend ÷ Impressions × 1000",
    formulaExpression: "ad_spend / impressions * 1000",
    variables: [
      { id: "ad_spend", label: "Ad Spend", metricKey: "cost" },
      { id: "impressions", label: "Impressions", metricKey: "impressions" },
    ],
    outputFormat: "currency",
  },
  {
    key: "ctr",
    label: "CTR",
    description: "Click-through rate — Clicks ÷ Impressions",
    formulaExpression: "clicks / impressions",
    variables: [
      { id: "clicks", label: "Clicks", metricKey: "clicks" },
      { id: "impressions", label: "Impressions", metricKey: "impressions" },
    ],
    outputFormat: "percentage",
  },
  {
    key: "conversion_rate",
    label: "Conversion Rate",
    description: "Conversions ÷ Clicks",
    formulaExpression: "conversions / clicks",
    variables: [
      { id: "conversions", label: "Conversions", metricKey: "conversions" },
      { id: "clicks", label: "Clicks", metricKey: "clicks" },
    ],
    outputFormat: "percentage",
  },
];
